import {
  Avatar as MuiAvatar,
  IconButton as MuiIconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  Zoom,
} from '@material-ui/core';
import { Check } from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
  Tag,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { VehicleFormDTO, carrierSearchTypes } from '../data/VehiclesDTO';
import { useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Clear, Search } from '@material-ui/icons';
import styled from 'styled-components';
import { useCarriers } from '../data/VehiclesAPI';
import type { ShipmentCarrierDTO } from '../data/VehiclesDTO';
import { getInitials } from 'shared/utils/TextUtils';
import { useFormikContext } from 'formik';
import { FIFTH_OF_A_SECOND } from 'shared/constants/NumberConstants';
import { useDebouncedValue } from 'shared/helpers/ReactHelpers';
import { SearchIcon } from 'shared/icons/SearchIcon';
import { CircleAlertIcon } from 'shared/icons/CircleAlertIcon';

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const IconButton = styled(MuiIconButton)`
  padding: 0;
`;

const CarrierRow = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 32px;
  cursor: pointer;
  background-color: ${({ $selected }) =>
    $selected ? ColorDynamic.Blue50 : 'transparent'};

  &:hover {
    background-color: ${({ $selected }) =>
      $selected ? ColorDynamic.Blue50 : ColorDynamic.Silver200};
  }
`;

const CarrierListContainer = styled.div`
  height: 100%;
  overflow-y: auto;
`;

const CarrierListInner = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  position: relative;
`;

const CarrierVirtualItem = styled.div<{ $offset: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  transform: translateY(${({ $offset }) => $offset}px);
`;

const Avatar = styled(MuiAvatar)`
  width: 42.75px;
  height: 45px;
`;

interface CarrierListItemProps {
  carrier: ShipmentCarrierDTO;
  isSelected: boolean;
  onClick: () => void;
}

function CarrierListItem({
  carrier,
  isSelected,
  onClick,
}: CarrierListItemProps) {
  return (
    <CarrierRow $selected={isSelected} onClick={onClick}>
      <Avatar>{getInitials(carrier.name)}</Avatar>
      <Stack space="xsmall">
        <Typography variant="h5">{carrier.name}</Typography>
        {!!carrier.partnered_loads_count && (
          <Tag variant="subtle" color="green">
            Partnered on {carrier.partnered_loads_count} loads
          </Tag>
        )}
      </Stack>
      {isSelected && <Check htmlColor={ColorDynamic.Dark100} />}
    </CarrierRow>
  );
}

function CarrierListSkeleton() {
  return (
    <div>
      {Array.from({ length: 5 }).map((_, i) => (
        <CarrierRow key={i}>
          <Skeleton variant="circle" width={42.75} height={45} />
          <Stack space="xxsmall">
            <Skeleton variant="text" width={200} height={22} />
            <Skeleton variant="text" width={140} height={20} />
          </Stack>
        </CarrierRow>
      ))}
    </div>
  );
}

export function VehicleCarrierSearch() {
  const inputRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const [carrierSearchFilter, setCarrierSearchFilter] =
    useState<(typeof carrierSearchTypes)[number]>('name_usdot');
  const { values, setFieldValue } = useFormikContext<VehicleFormDTO>();

  const [text, setText] = useState<string>('');
  const debouncedSearch = useDebouncedValue(text, FIFTH_OF_A_SECOND);

  const {
    data: carriers,
    isLoading,
    isError,
  } = useCarriers(
    {
      name_usdot:
        carrierSearchFilter === 'name_usdot' ? debouncedSearch : undefined,
      email: carrierSearchFilter === 'email' ? debouncedSearch : undefined,
      mc_number:
        carrierSearchFilter === 'mc_number' ? debouncedSearch : undefined,
      phone_number:
        carrierSearchFilter === 'phone_number' ? debouncedSearch : undefined,
    },
    { enabled: debouncedSearch.length >= 3 },
  );

  const virtualizer = useVirtualizer({
    count: carriers?.length ?? 0,
    getScrollElement: () => listRef.current,
    estimateSize: () => 72,
    overscan: 5,
  });

  return (
    <Stack space="small">
      <Box paddingLeft="large" paddingRight="large">
        <Stack space="small">
          <Typography variant="h4">
            Select the carrier to dispatch this load
          </Typography>
          <Columns space="xsmall">
            <Column width="content">
              <Select
                variant="outlined"
                value={carrierSearchFilter}
                onChange={({
                  target: { value },
                }: React.ChangeEvent<{
                  value: (typeof carrierSearchTypes)[number];
                }>) => {
                  setCarrierSearchFilter(value);
                }}
              >
                <MenuItem value="name_usdot">Name & USDOT</MenuItem>
                <MenuItem value="mc_number">MC Number</MenuItem>
                <MenuItem value="email">Email</MenuItem>
                <MenuItem value="phone_number">Phone Number</MenuItem>
              </Select>
            </Column>
            <Column width="fluid">
              <TextField
                inputRef={inputRef}
                value={text}
                fullWidth={true}
                variant="outlined"
                placeholder="3 symbols to search a carrier"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconButton
                        onClick={() => {
                          if (text) {
                            setText('');
                          }
                          if (inputRef.current) {
                            inputRef.current.focus();
                          }
                        }}
                        edge="end"
                        size="small"
                      >
                        <IconWrapper>
                          <Zoom key={text ? 'clear' : 'search'} in={true}>
                            {text ? <Clear /> : <Search />}
                          </Zoom>
                        </IconWrapper>
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onChange={({
                  target: { value },
                }: React.ChangeEvent<{
                  value: string;
                }>) => {
                  setText(value);
                }}
              />
            </Column>
          </Columns>
        </Stack>
      </Box>
      {isLoading && <CarrierListSkeleton />}
      {!isLoading && carriers && carriers.length > 0 && (
        <CarrierListContainer ref={listRef}>
          <CarrierListInner $height={virtualizer.getTotalSize()}>
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const carrier = carriers[virtualItem.index];
              if (!carrier) return null;
              return (
                <CarrierVirtualItem
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  $offset={virtualItem.start}
                >
                  <CarrierListItem
                    carrier={carrier}
                    isSelected={values.carrier_guid === carrier.guid}
                    onClick={() => {
                      setFieldValue('carrier_guid', carrier.guid);
                      setFieldValue('carrier_name', carrier.name);
                    }}
                  />
                </CarrierVirtualItem>
              );
            })}
          </CarrierListInner>
        </CarrierListContainer>
      )}

      {isError && (
        <VehicleSearchState
          title="Something Went Wrong"
          description="Unable to load carriers."
          icon={<CircleAlertIcon />}
        />
      )}
      {!isError && (!carriers || carriers.length === 0) && (
        <VehicleSearchState
          title="Search for Carrier"
          description="Type at least 3 symbols to start searching for the carrier."
          icon={<SearchIcon />}
        />
      )}
    </Stack>
  );
}

interface VehicleCarrierSearchProps {
  icon?: React.ReactNode;
  title?: string;
  description?: string;
}

function VehicleSearchState({
  icon,
  title,
  description,
}: VehicleCarrierSearchProps) {
  return (
    <Box width="100%" paddingTop="large">
      <Inline horizontalAlign="center">
        <Stack space="xsmall" align="center">
          {icon}
          <Typography variant="h3">{title}</Typography>
          <Typography color="textSecondary">{description}</Typography>
        </Stack>
      </Inline>
    </Box>
  );
}
